package com.karawek.core.ui.components

import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import com.karawek.core.ui.theme.CoreAlphas
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.util.PhoneNumberFormatter

/**
 * A phone number text field that automatically formats the input as (000) 000-0000
 * and stores/returns the value in E.164 format (+15551234567).
 *
 * @param value The phone number in E.164 format (e.g., "+15551234567")
 * @param onValueChange Callback with phone number in E.164 format (e.g., "+15551234567")
 * @param label The label for the text field
 * @param placeholder The placeholder text
 * @param modifier The modifier to be applied to the text field
 * @param enabled Whether the text field is enabled
 * @param error Error message to display, if any
 */
@Composable
fun CorePhoneTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    placeholder: String = "",
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    error: String? = null,
) {
    // Convert E.164 to display format for initial display
    val displayValue = if (value.isNotEmpty()) {
        PhoneNumberFormatter.toDisplayFormat(value)
    } else {
        ""
    }

    var textFieldValue by remember(value) {
        mutableStateOf(
            TextFieldValue(
                text = displayValue,
                selection = TextRange(displayValue.length)
            )
        )
    }

    // Update text field value when external value changes
    LaunchedEffect(value) {
        val formatted = if (value.isNotEmpty()) {
            PhoneNumberFormatter.toDisplayFormat(value)
        } else {
            ""
        }
        if (textFieldValue.text != formatted) {
            textFieldValue = TextFieldValue(
                text = formatted,
                selection = TextRange(formatted.length)
            )
        }
    }

    OutlinedTextField(
        value = textFieldValue,
        onValueChange = { newTextFieldValue ->
            val digitsOnly = newTextFieldValue.text.filter { it.isDigit() }.take(10)
            val formatted = PhoneNumberFormatter.formatAsTyping(digitsOnly)

            // Calculate new cursor position
            val oldDigitCount = textFieldValue.text.take(textFieldValue.selection.start).count { it.isDigit() }
            val newDigitCount = newTextFieldValue.text.take(newTextFieldValue.selection.start).count { it.isDigit() }
            val digitDiff = newDigitCount - oldDigitCount

            var newCursorPos = 0
            var digitsSeen = 0
            for (i in formatted.indices) {
                if (formatted[i].isDigit()) {
                    digitsSeen++
                    if (digitsSeen > oldDigitCount + digitDiff) break
                }
                newCursorPos = i + 1
            }

            textFieldValue = TextFieldValue(
                text = formatted,
                selection = TextRange(newCursorPos.coerceIn(0, formatted.length))
            )

            // Convert to E.164 before calling onValueChange
            // Only update if we have exactly 10 digits (valid phone number)
            // Otherwise keep the previous value to prevent clearing
            if (digitsOnly.length == 10) {
                val e164 = PhoneNumberFormatter.toE164(digitsOnly)
                onValueChange(e164 ?: "")
            } else if (digitsOnly.isEmpty()) {
                onValueChange("")
            }
        },
        label = { Text(label) },
        placeholder = { Text(placeholder) },
        modifier = modifier,
        enabled = enabled,
        isError = error != null,
        textStyle = CoreTypography.body,
        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
        supportingText = {
            error?.let {
                Text(
                    text = it,
                    style = CoreTypography.caption,
                    color = CoreTheme.colors.error,
                )
            }
        },
        colors = androidx.compose.material3.OutlinedTextFieldDefaults.colors(
            focusedBorderColor = CoreTheme.colors.primary,
            focusedLabelColor = CoreTheme.colors.primary,
            errorBorderColor = CoreTheme.colors.error,
            errorLabelColor = CoreTheme.colors.error,
            disabledBorderColor = CoreTheme.colors.divider.copy(alpha = CoreAlphas.Disabled),
            disabledLabelColor = CoreTheme.colors.textSecondary.copy(alpha = CoreAlphas.Disabled),
        ),
    )
}


